/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Clock3, Mail, MapPin, Phone } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Navigation } from "@/components/customCopmonent/landingSection/navigation";
import { FooterSection } from "@/components/customCopmonent/landingSection/footer-section";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
	Card,
	GridLayout,
	PageHeader,
	PageShell,
	RevealOnScroll,
	SectionDivider,
	SectionWrapper,
} from "./ContactUs.page-primitives";
import DotGrid from "../components/customCopmonent/DotGrid";
import { PageHeaderHero, PAGE_HERO_CLASS } from "../components/customCopmonent/Text/PageHeaderHero";
import ContactPage from "../components/customCopmonent/ContactPage";
import Seo from "@/components/Seo";

const contactSchema = z.object({
	name: z.string().min(2, "Please enter your name."),
	email: z.string().email("Enter a valid work email."),
	company: z.string().min(2, "Please enter your company name."),
	phone: z.string().min(7, "Please enter a valid phone number."),
	message: z.string().min(20, "Please share a few project details."),
});

type ContactFormValues = z.infer<typeof contactSchema>;

const pageData = {
	title: "Contact Us",
	bgimage: "/images/header/ContactU.jpeg",

	description:
		"Use the form for RFQs, technical discussions, qualification requests, or early-stage project conversations.",
	meta: ["Request a Quote", "Technical Consultation", "After-Sales Support"],
	details: [
		{
			title: "Manufacturing & Office",
			value: "Plot 12, Belagavi Industrial Corridor, Karnataka, India",
			icon: MapPin,
		},
		{
			title: "Email",
			value: "info@positron-he.com",
			icon: Mail,
		},
		{
			title: "Phone",
			value: "+91 831-2411834",
			icon: Phone,
		},
		{
			title: "Business Hours",
			value: "Monday to Saturday, 9:00 AM to 6:30 PM",
			icon: Clock3,
		},
	],
	cta: {
		title: "Talk to our engineers about your next thermal requirement.",
		description:
			"Early technical alignment usually leads to stronger equipment selection, clearer documentation, and fewer downstream compromises.",
	},
};

const ContactUs = () => {
	const { t } = useTranslation();
	const [isSubmitted, setIsSubmitted] = useState(false);

	const form = useForm<ContactFormValues>({
		resolver: zodResolver(contactSchema),
		defaultValues: {
			name: "",
			email: "",
			company: "",
			phone: "",
			message: "",
		},
	});

	const onSubmit = async (_values: ContactFormValues) => {
		await new Promise((resolve) => setTimeout(resolve, 900));
		setIsSubmitted(true);
		form.reset();
		window.setTimeout(() => setIsSubmitted(false), 2400);
	};

	return (
		<PageShell>
			<Seo
				titleKey="seo.contact.title"
				descriptionKey="seo.contact.description"
				path="/contact-us"
			/>
			<Navigation />{" "}
			<SectionWrapper className={PAGE_HERO_CLASS}>
				<div className="absolute inset-0 ">
					<div
						className="absolute bottom-3 inset-0 opacity-35 scale-105"
						style={{
							backgroundImage: `url(${pageData.bgimage})`,
							backgroundSize: "cover",
							backgroundPosition: "center",
						}}
					/>
					<DotGrid
						dotSize={4}
						gap={15}
						baseColor="#3b0092"
						activeColor="#f09a38"
						proximity={100}
						shockRadius={200}
						shockStrength={3}
						resistance={750}
						returnDuration={1.5}
					/>
				</div>
				<PageHeaderHero
					// align="center"
					title={t("contact.pageTitle")}
					description={t("contact.pageTagline")}
					meta={[
						t("contact.meta.rfq"),
						t("contact.meta.technicalDiscussion"),
						t("contact.meta.projectSupport"),
					]}
				/>
			</SectionWrapper>
			<SectionDivider />
			<ContactPage />
			<FooterSection />
		</PageShell>
	);
};

interface FieldProps {
	control: ReturnType<typeof useForm<ContactFormValues>>["control"];
	name: "name" | "email" | "company" | "phone" | "message";
	label: string;
	type?: string;
}

function FloatingField({ control, name, label, type = "text" }: FieldProps) {
	return (
		<FormField
			control={control}
			name={name}
			render={({ field }) => (
				<FormItem>
					<div className="relative">
						<FormControl>
							<Input
								{...field}
								type={type}
								placeholder=" "
								className="peer h-13 rounded-2xl border-slate-200 bg-slate-50 px-4 pt-5 text-slate-900 focus-visible:border-[#2d2f92] focus-visible:ring-[#2d2f92]/20"
							/>
						</FormControl>
						<label
							className={`pointer-events-none absolute left-4 origin-left text-sm text-slate-500 transition-all duration-200 peer-focus:top-2 peer-focus:scale-90 ${
								field.value ? "top-2 scale-90" : "top-4 scale-100"
							}`}>
							{label}
						</label>
					</div>
					<FormMessage className="text-xs text-red-600" />
				</FormItem>
			)}
		/>
	);
}

function FloatingTextarea({ control, name, label }: FieldProps) {
	return (
		<FormField
			control={control}
			name={name}
			render={({ field }) => (
				<FormItem>
					<div className="relative">
						<FormControl>
							<Textarea
								{...field}
								placeholder=" "
								className="peer min-h-36 rounded-3xl border-slate-200 bg-slate-50 px-4 pt-6 text-slate-900 focus-visible:border-[#2d2f92] focus-visible:ring-[#2d2f92]/20"
							/>
						</FormControl>
						<label
							className={`pointer-events-none absolute left-4 origin-left text-sm text-slate-500 transition-all duration-200 peer-focus:top-3 peer-focus:scale-90 ${
								field.value ? "top-3 scale-90" : "top-5 scale-100"
							}`}>
							{label}
						</label>
					</div>
					<FormMessage className="text-xs text-red-600" />
				</FormItem>
			)}
		/>
	);
}

export default ContactUs;
